/**
 * src/components/SubscriptionPaymentForm.jsx — the embedded studio
 * subscription payment form.
 *
 * Sibling of PaymentForm.jsx (the embedded client-deposit form), built the
 * same way — a Checkout Session created with `ui_mode: 'elements'`
 * (services/stripe.js's createSubscriptionCheckout), Stripe's Elements
 * mounted against its client secret. Kept as its own component rather than
 * a generalised version of PaymentForm: the copy, the lack of a
 * studio-branded accent colour (this is an owner-facing app screen, not a
 * client-facing branded page), and the success/error handling context are
 * different enough that sharing one component would mean threading a pile
 * of conditionals through it for two callers.
 *
 * Same PCI posture as PaymentForm.jsx: the card fields are Stripe's own
 * iframes, never our DOM or our Worker. Do not replace PaymentElement with
 * a plain `<input>`.
 */
import {
  CheckoutElementsProvider,
  PaymentElement,
  useCheckoutElements,
} from '@stripe/react-stripe-js/checkout';
import { useState } from 'react';

import { getStripe } from '../lib/stripe.js';
import { ErrorBanner } from './ui.jsx';

/**
 * @param {object} props
 * @param {string} props.publishableKey Stripe publishable key, from the API.
 * @param {string} props.clientSecret Checkout Session client secret.
 * @param {() => void} props.onSuccess Called on success without a redirect.
 * @returns {JSX.Element}
 */
export default function SubscriptionPaymentForm({ publishableKey, clientSecret, onSuccess }) {
  return (
    <CheckoutElementsProvider
      stripe={getStripe(publishableKey)}
      options={{
        clientSecret,
        elementsOptions: {
          appearance: {
            theme: 'flat',
            variables: {
              colorPrimary: '#1a1a1a',
              colorBackground: '#ffffff',
              colorText: '#1a1a1a',
              colorDanger: '#b23a48',
              borderRadius: '8px',
              fontFamily:
                '-apple-system, BlinkMacSystemFont, Segoe UI, Helvetica, Arial, sans-serif',
              fontSizeBase: '14px',
            },
          },
        },
      }}
    >
      <CardFields onSuccess={onSuccess} />
    </CheckoutElementsProvider>
  );
}

/**
 * The inner form. Separate component because `useCheckoutElements` only
 * works inside the provider.
 *
 * @param {{ onSuccess: () => void }} props
 * @returns {JSX.Element}
 */
function CardFields({ onSuccess }) {
  const result = useCheckoutElements();
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  if (result.type === 'loading') {
    return <p className="muted small loading-pulse">Loading payment form…</p>;
  }
  if (result.type === 'error') {
    return (
      <ErrorBanner>
        {result.error.message || 'The payment form could not be loaded. Please try again.'}
      </ErrorBanner>
    );
  }

  const { checkout } = result;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);

    // No returnUrl here: the Checkout Session already has return_url set
    // from creation (routes/billing.js) — confirm() rejects a second one
    // ("You cannot provide returnUrl to confirm() when return_url was
    // already provided when creating the Checkout Session").
    const confirmation = await checkout.confirm({
      // Only leave the page when the payment method genuinely requires it —
      // a plain card payment resolves here, without navigating away.
      redirect: 'if_required',
    });

    if (confirmation.type === 'error') {
      const { type, message } = confirmation.error;
      setError(
        type === 'card_error' || type === 'validation_error'
          ? message
          : 'Something went wrong starting your subscription. Your card has not been charged.',
      );
      setBusy(false);
      return;
    }

    // No redirect was needed and the payment is through. The subscription is
    // still confirmed active by the billing webhook rather than by this
    // callback — the caller reflects that with a "confirming…" state, not
    // an instant unlock.
    onSuccess?.();
  };

  return (
    <form className="stack" onSubmit={submit}>
      <PaymentElement />
      <ErrorBanner>{error}</ErrorBanner>
      <button type="submit" className="btn btn-primary" disabled={busy}>
        {busy ? 'Starting subscription…' : 'Start subscription'}
      </button>
      <p className="muted small">
        Card details are handled by Stripe and never touch this app&rsquo;s systems.
      </p>
    </form>
  );
}
